import { useQuery } from "@tanstack/react-query";
import { feedbacksService } from "@/services/feedbacks-service";
import type { ReviewsSegment } from "../components/reviews/types";
import {
  REVIEWS_FEEDBACK_FETCH_LIMIT,
  buildPreCheckoutTrendFromCheckoutFeedbacks,
  mapCheckoutFeedbacksToCategoryBreakdowns,
  mapCheckoutFeedbacksToTableRows,
  mapCommunicationFeedbacksToTableRows,
  mapRequestFeedbacksToTableRows,
  toFeedbackDateRange,
} from "../components/reviews/reviewsFeedbacksMappers";
import { createEmptyCategoryBreakdowns } from "../components/reviews/reviewsTabDefaults";
import type {
  CategoryKey,
  CategoryRatingRow,
  PreCheckoutTrendPoint,
  ReviewTableRow,
} from "../components/reviews/types";

export const reviewsFeedbacksKeys = {
  all: ["reviews-feedbacks"] as const,
  list: (hotelId: string, from: string, to: string, segment: ReviewsSegment) =>
    [...reviewsFeedbacksKeys.all, hotelId, from, to, segment] as const,
};

export type ReviewsFeedbacksResult = {
  relatedReviews: ReviewTableRow[];
  categoryBreakdowns: Record<CategoryKey, CategoryRatingRow[]>;
  preCheckoutTrend: PreCheckoutTrendPoint[];
};

const EMPTY_RESULT: ReviewsFeedbacksResult = {
  relatedReviews: [],
  categoryBreakdowns: createEmptyCategoryBreakdowns(),
  preCheckoutTrend: [],
};

export function useReviewsFeedbacks(
  hotelId: string,
  from: string,
  to: string,
  segment: ReviewsSegment,
  enabled: boolean,
) {
  const dateRange = toFeedbackDateRange(from, to);

  return useQuery({
    queryKey: reviewsFeedbacksKeys.list(hotelId, from, to, segment),
    queryFn: async (): Promise<ReviewsFeedbacksResult> => {
      const baseQuery = {
        hotelId,
        page: 1,
        limit: REVIEWS_FEEDBACK_FETCH_LIMIT,
        ...dateRange!,
      };

      if (segment === "reservations") {
        const response =
          await feedbacksService.getAllCheckoutFeedbacks(baseQuery);

        return {
          relatedReviews: mapCheckoutFeedbacksToTableRows(response.data),
          categoryBreakdowns: mapCheckoutFeedbacksToCategoryBreakdowns(
            response.data,
          ),
          preCheckoutTrend: buildPreCheckoutTrendFromCheckoutFeedbacks(
            response.data,
            from,
            to,
          ),
        };
      }

      if (segment === "requests") {
        const response =
          await feedbacksService.getAllRequestFeedbacks(baseQuery);

        return {
          relatedReviews: mapRequestFeedbacksToTableRows(response.data),
          categoryBreakdowns: createEmptyCategoryBreakdowns(),
          preCheckoutTrend: [],
        };
      }

      const response =
        await feedbacksService.getAllCommunicationFeedbacks(baseQuery);

      return {
        relatedReviews: mapCommunicationFeedbacksToTableRows(response.data),
        categoryBreakdowns: createEmptyCategoryBreakdowns(),
        preCheckoutTrend: [],
      };
    },
    enabled: enabled && Boolean(hotelId) && dateRange != null,
    placeholderData: EMPTY_RESULT,
  });
}
